import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { courseService } from '../../../services/courses.js';
import type { CourseCreateInput, CourseUpdateInput } from '../../../services/courses.js';
import { XP_RULES, type CourseResponse, type SharedCourseResponse } from '@xdface-vault/shared';
import { useGamificationStore } from '../../../stores/gamification-store.js';

// ---------- Query keys (shared by useModules.ts and useLessons.ts) ----------
export const COURSE_KEYS = {
  all: ['courses'] as const,
  shared: ['courses', 'shared'] as const,
  detail: (id: string) => ['courses', id] as const,
  lessons: (courseId: string) => ['courses', courseId, 'lessons'] as const,
  modules: (courseId: string) => ['courses', courseId, 'modules'] as const,
  progress: (courseId: string) => ['courses', courseId, 'progress'] as const,
  highlights: (courseId: string, lessonNum: number) =>
    ['courses', courseId, 'lessons', lessonNum, 'highlights'] as const,
};

// ---------- Queries ----------
export function useCourseList() {
  return useQuery<CourseResponse[]>({
    queryKey: COURSE_KEYS.all,
    queryFn: () => courseService.listCourses(),
    staleTime: 1000 * 60 * 2,
  });
}

export function useSharedCourseList() {
  return useQuery<SharedCourseResponse[]>({
    queryKey: COURSE_KEYS.shared,
    queryFn: () => courseService.listSharedCourses(),
    staleTime: 1000 * 60 * 5, // Shared courses change less frequently
  });
}

export function useCourse(id: string | undefined) {
  return useQuery<CourseResponse & { isShared?: boolean; ownerEmail?: string }>({
    queryKey: COURSE_KEYS.detail(id ?? ''),
    queryFn: () => courseService.getCourse(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 2,
  });
}

// ---------- Course mutations ----------
export function useCreateCourse() {
  const qc = useQueryClient();
  return useMutation<CourseResponse, Error, CourseCreateInput>({
    mutationFn: (input) => courseService.createCourse(input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: COURSE_KEYS.all });
      const prev = qc.getQueryData<CourseResponse[]>(COURSE_KEYS.all);
      const optimistic: CourseResponse = {
        id: `temp-${Date.now()}`,
        title: input.title,
        description: input.description,
        tags: input.tags ?? [],
        lessonsCount: 0,
        published: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        version: 1,
      };
      qc.setQueryData<CourseResponse[]>(COURSE_KEYS.all, (old) => [optimistic, ...(old ?? [])]);
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      const context = ctx as { prev?: CourseResponse[] } | undefined;
      if (context?.prev !== undefined) {
        qc.setQueryData(COURSE_KEYS.all, context.prev);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: COURSE_KEYS.all });
    },
  });
}

export function useUpdateCourse() {
  const qc = useQueryClient();
  return useMutation<CourseResponse, Error, { id: string; input: CourseUpdateInput }>({
    mutationFn: ({ id, input }) => courseService.updateCourse(id, input),
    onMutate: async ({ id, input }) => {
      await qc.cancelQueries({ queryKey: COURSE_KEYS.detail(id) });
      const prev = qc.getQueryData<CourseResponse>(COURSE_KEYS.detail(id));
      if (prev) {
        const { source, coverImageKey, ...rest } = input;
        qc.setQueryData<CourseResponse>(COURSE_KEYS.detail(id), {
          ...prev,
          ...rest,
          ...(source !== undefined ? { source: source ?? undefined } : {}),
          ...(coverImageKey !== undefined ? { coverImageKey: coverImageKey ?? undefined } : {}),
          updatedAt: new Date().toISOString(),
        });
      }
      return { prev };
    },
    onError: (_err, { id }, ctx) => {
      const context = ctx as { prev?: CourseResponse } | undefined;
      if (context?.prev !== undefined) {
        qc.setQueryData(COURSE_KEYS.detail(id), context.prev);
      }
    },
    onSettled: (data, _err, { id }) => {
      qc.invalidateQueries({ queryKey: COURSE_KEYS.detail(id) });
      // Update list cache in-place instead of refetching all courses
      if (data) {
        qc.setQueryData<CourseResponse[]>(COURSE_KEYS.all, (old) =>
          old?.map((c) => (c.id === id ? { ...c, ...data } : c)),
        );
      }
    },
  });
}

export function useDeleteCourse() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => courseService.deleteCourse(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: COURSE_KEYS.all });
      const prev = qc.getQueryData<CourseResponse[]>(COURSE_KEYS.all);
      qc.setQueryData<CourseResponse[]>(COURSE_KEYS.all, (old) =>
        (old ?? []).filter((c) => c.id !== id),
      );
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      const context = ctx as { prev?: CourseResponse[] } | undefined;
      if (context?.prev !== undefined) {
        qc.setQueryData(COURSE_KEYS.all, context.prev);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: COURSE_KEYS.all });
    },
  });
}

// ---------- Share / grants ----------
export function useShareCourse() {
  const qc = useQueryClient();
  return useMutation<
    { shareUrl: string; token: string; expiresAt: string; ttlDays: number },
    Error,
    { courseId: string; ttlDays: 30 | 60 | 90 | 180 | 365 }
  >({
    mutationFn: ({ courseId, ttlDays }) => courseService.shareCourse(courseId, ttlDays),
    onSuccess: (_data, { courseId }) => {
      useGamificationStore.getState().addXP(XP_RULES.SHARING_UNLOCKED);
      qc.invalidateQueries({ queryKey: ['gamification'] });
      qc.invalidateQueries({ queryKey: COURSE_KEYS.detail(courseId) });
    },
  });
}

export function useRevokeCourseShare() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (courseId) => courseService.revokeCourseShare(courseId),
    onSuccess: (_data, courseId) => {
      qc.invalidateQueries({ queryKey: COURSE_KEYS.detail(courseId) });
    },
  });
}

export function useGrantCourseAccess() {
  const qc = useQueryClient();
  return useMutation<
    { shared: boolean; recipient: string; courseTitle: string },
    Error,
    { courseId: string; email: string }
  >({
    mutationFn: ({ courseId, email }) => courseService.grantCourseAccess(courseId, email),
    onSuccess: (_data, { courseId }) => {
      qc.invalidateQueries({ queryKey: COURSE_KEYS.detail(courseId) });
    },
  });
}

export function useRevokeGrantCourseAccess() {
  const qc = useQueryClient();
  return useMutation<
    { revoked: boolean; recipient: string; courseTitle: string },
    Error,
    { courseId: string; email: string }
  >({
    mutationFn: ({ courseId, email }) => courseService.revokeGrantCourseAccess(courseId, email),
    onSuccess: (_data, { courseId }) => {
      qc.invalidateQueries({ queryKey: COURSE_KEYS.detail(courseId) });
    },
  });
}

// ---------- Facade: re-export module & lesson hooks ----------
export {
  useCourseModules,
  useCreateModule,
  useUpdateModule,
  useDeleteModule,
  useReorderModules,
} from './useModules.js';
export {
  useCourseLessons,
  useLessonContent,
  useCreateLesson,
  useUpdateLesson,
  useDeleteLesson,
  useReorderLessons,
  useLessonProgress,
  useMarkLessonComplete,
  useUnmarkLessonComplete,
  useLessonHighlights,
  useCreateHighlight,
  useUpdateHighlight,
  useDeleteHighlight,
} from './useLessons.js';
